import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { DateFormatPreset, NumberFormatPreset } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { TsrctContractDemandListItem } from '@/generated/gql/graphql';
import {
  ChipInfoCell,
  CurrencyCell,
  DataGridConfig,
  DataGridLinkVariant,
  DateRangeCell,
  DateRangeCellValue,
  GridAlignmentType,
  LinkCell,
  LinkCellValue,
  TooltipCell,
  UseDataGridConfig,
  cellFactory,
  dateFormatterFactory,
  gridColDefFactory,
  joinFormatterFactory,
  numberFormatterFactory,
} from '~/components/DataGridCustom';
import { ContractDemandStatusChipCell } from '~/components/DataGridCustom/cells/ContractDemandStatusChipCell/ContractDemandStatusChipCell';
import { CuriosityLinkChipCell } from '~/components/DataGridCustom/cells/CuriosityLinkChip/CuriosityLinkChipCell';
import { UserChipCell } from '~/components/DataGridCustom/cells/UserChipCell/UserChipCell.tsx';
import { capabilitiesDetailsGetter } from '~/components/DataGridWidget/DataGridWidget.util';
import { RouteId } from '~/routes';

import { ContractDemandDataGridField } from './ContractDemandList.types';

export const useContractDemandDataGridConfig: UseDataGridConfig<TsrctContractDemandListItem> = () => {
  const { t } = useTranslation();
  const { t: tColumn } = useTranslation(undefined, { keyPrefix: 'ContractDemandDataGrid.column' });

  return useMemo(() => {
    const createColumn = gridColDefFactory({ tColumn, t });
    return {
      sortModel: [{ field: ContractDemandDataGridField.SEQUENCE_NUMBER, sort: 'desc' }],
      columnVisibilityModel: {
        [ContractDemandDataGridField.TOTAL_CASE_COUNT]: false,
        [ContractDemandDataGridField.NEGOTIATED_CASE_COUNT]: false,
        [ContractDemandDataGridField.OPEN_CASE_COUNT]: false,
        [ContractDemandDataGridField.TAG_IDS]: false,
      },
      columns: [
        createColumn({
          field: ContractDemandDataGridField.SEQUENCE_NUMBER,
          renderCell: cellFactory(LinkCell),
          valueGetter: (value, row): LinkCellValue => ({
            variant: DataGridLinkVariant.PRIMARY,
            to: RouteId.contractDemandDetail(row.id),
            label: value,
          }),
          valueFormatter: (value: { label: string }) => value?.label,
          minWidth: 100,
        }),

        createColumn({
          field: ContractDemandDataGridField.STATUS,
          renderCell: cellFactory(ContractDemandStatusChipCell),
          minWidth: 150,
        }),
        createColumn({
          field: ContractDemandDataGridField.DESCRIPTION,
          renderCell: cellFactory(TooltipCell),
          minWidth: 300,
        }),
        createColumn({
          field: ContractDemandDataGridField.RESPONSIBLE_USER_ID,
          renderCell: cellFactory(UserChipCell, { ns: CuriosityNamespaceType.INTERNAL_USER }),
          minWidth: 200,
          sortable: false,
        }),
        createColumn({
          field: ContractDemandDataGridField.COMMODITY_GROUP_ID,
          renderCell: cellFactory(CuriosityLinkChipCell, { chipVariant: 'outlined' as const }),
          minWidth: 200,
          sortable: false,
        }),
        createColumn({
          field: ContractDemandDataGridField.TOTAL_VALUE,
          renderCell: cellFactory(CurrencyCell, ({ row }) => ({ currency: row.currency })),
          minWidth: 150,
        }),
        createColumn({
          field: ContractDemandDataGridField.CREATED_AT,
          valueFormatter: dateFormatterFactory({ preset: DateFormatPreset.TWO_DIGIT_TIME }),
          minWidth: 170,
          align: GridAlignmentType.RIGHT,
        }),
        createColumn({
          minWidth: 200,
          field: ContractDemandDataGridField.VALID_FROM,
          renderCell: cellFactory(DateRangeCell),
          valueGetter: (_value, row): DateRangeCellValue => ({
            from: row.validFrom,
            to: row.validTo,
          }),
        }),
        createColumn({
          field: ContractDemandDataGridField.TOTAL_CASE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 100,
        }),
        createColumn({
          field: ContractDemandDataGridField.NEGOTIATED_CASE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 100,
        }),
        createColumn({
          field: ContractDemandDataGridField.OPEN_CASE_COUNT,
          valueFormatter: numberFormatterFactory({ preset: NumberFormatPreset.INTEGER }),
          minWidth: 100,
        }),
        createColumn({
          field: ContractDemandDataGridField.TAG_IDS,
          renderCell: cellFactory(ChipInfoCell, {
            detailsGetter: capabilitiesDetailsGetter(t),
          }),
          valueFormatter: joinFormatterFactory(),
          sortable: false,
          minWidth: 150,
        }),
      ],
      exportModel: {
        [ContractDemandDataGridField.TAG_IDS]: {
          resolveCuriosity: CuriosityNamespaceType.CAPABILITY,
        },
        [ContractDemandDataGridField.RESPONSIBLE_USER_ID]: {
          resolveCuriosity: CuriosityNamespaceType.INTERNAL_USER,
        },
        [ContractDemandDataGridField.COMMODITY_GROUP_ID]: {
          resolveCuriosity: CuriosityNamespaceType.COMMODITY_GROUP,
        },
      },
    } as DataGridConfig<TsrctContractDemandListItem>;
  }, [t, tColumn]);
};
